import { useMemo, useState } from "react";
import { FileTextOutlined } from "@ant-design/icons";
import { App, Button, Empty } from "antd";
import { downloadOrderFormPdf, type ChooseProduct, type FormData } from "@ifa/utils";

import formStyles from "./ZuuFnaSection.module.css";
import sectionStyles from "./ZuuSection.module.css";
import { buildZuuFnaView } from "./zuuFnaView.js";

export type ZuuFnaDataSource = {
  financial?: {
    financialAnalysis?: FormData[];
    premiumFinanceAnalysis?: FormData[];
    suggestionsAndReasons?: FormData[];
    chooseProducts?: ChooseProduct[];
  } | null;
  historyFnaList?: Array<unknown> | null;
  sourceOrderId?: number;
};

const PREMIUM_FINANCE_REPAYMENT_SOURCE_LABELS: Record<string, string> = {
  A: "儲蓄",
  B: "薪俸",
  C: "流動資產",
  D: "其他",
};

const RECOMMENDATION_LABELS: Record<string, string> = {
  A: "此建議考慮到申請人的理財目標、全面保障的需要，其需要重要性及申請人的財務預算而作出，申請人期望以上各方面取得平衡。",
  B: "產品推介與申請人財務需要分析選項不相符之原因。",
};

const RECOMMENDATION_REASON_LABELS: Record<string, string> = {
  B1_A: "申請人在進行財務需要分析時沒有表示有此選購產品的目標。",
  B1_B: "最終選購產品的得益／保障年期與申請人的目標得益／保障年期／實現目標金額的預期時間不符。",
  B1_C: "最終選購產品的人壽保障額與受保人需要的額外人壽保障額不符。",
  B1_D: "最終選購產品的危疾保障額與受保人需要的額外危疾保障額不符。",
  B1_E: "最終選購產品的目標儲蓄金額與申請人需要的額外目標儲蓄金額及／或額外投資回報金額不符。",
  B1_F: "最終選購產品達至所述之投資回報金額的所需儲蓄／投資年期與申請人期望的年期不符。",
  B1_G: "其他",
  B2_A: "我（中介人）已向申請人解釋其最終選購的保險產品並不適合其需要，但應申請人的喜好而作出介紹。",
  B2_B: "我（中介人）已向申請人解釋其最終選購的保險產品並不適合其需要，但申請人經財務需要分析後要求介紹。",
  B2_C: "我（中介人）已向申請人解釋其最終選購的保險產品並不適合其需要，但申請人因其親友購買了同一產品而要求介紹。",
  B2_D: "縱使申請人最終選購的保險產品之保額／年期／回報與其財務需要分析所透露的資料不相符，我（中介人）認為此建議產品適合申請人。",
  B2_E: "其他",
};

function findFormData(list: FormData[] | undefined, question: string) {
  return list?.find((item) => item.question === question);
}

function getSelectedKeys(list: FormData[] | undefined, question: string) {
  return (
    findFormData(list, question)?.items
      ?.filter((item) => item.value === true)
      .map((item) => item.key)
      .filter((item): item is string => Boolean(item)) ?? []
  );
}

function getSelectedKey(list: FormData[] | undefined, question: string) {
  return getSelectedKeys(list, question)[0] ?? "";
}

function getInputValue(list: FormData[] | undefined, question: string) {
  const rawValue = findFormData(list, question)?.items?.find(
    (item) => typeof item.value === "string" || typeof item.value === "number",
  )?.value;

  return typeof rawValue === "string" || typeof rawValue === "number"
    ? String(rawValue)
    : "";
}

function formatSimpleValue(value?: string) {
  return value && value.trim().length > 0 ? value : "--";
}

function formatYesNoByKey(key: string, trueKey: string) {
  if (!key) return "--";
  return key === trueKey ? "是" : "否";
}

function buildPremiumFinanceSummary(premiumFinanceAnalysis: FormData[] | undefined) {
  const repaymentSourceKey = getSelectedKey(
    premiumFinanceAnalysis,
    "premiumFinancingRepaymentSource",
  );
  const repaymentSourceRemark = getInputValue(
    premiumFinanceAnalysis,
    "premiumFinancingRepaymentSource_D_remark",
  );

  const repaymentSource = repaymentSourceKey
    ? PREMIUM_FINANCE_REPAYMENT_SOURCE_LABELS[repaymentSourceKey] ??
      repaymentSourceKey
    : "--";

  return {
    repaymentSource:
      repaymentSourceKey === "D" && repaymentSourceRemark
        ? `${repaymentSource}：${repaymentSourceRemark}`
        : repaymentSource,
    outstandingMortgageAmount: formatSimpleValue(
      getInputValue(premiumFinanceAnalysis, "outstandingMortgageAmount"),
    ),
    outstandingOtherLoansAmount: formatSimpleValue(
      getInputValue(premiumFinanceAnalysis, "outstandingOtherLoansAmount"),
    ),
    willingToProvidePremiumFinancingInfo: formatYesNoByKey(
      getSelectedKey(premiumFinanceAnalysis, "willingToProvidePremiumFinancingInfo"),
      "A",
    ),
    lenderName: formatSimpleValue(
      getInputValue(
        premiumFinanceAnalysis,
        "willingToProvidePremiumFinancingInfo_A_nameOfLender",
      ),
    ),
    loanAmount: formatSimpleValue(
      getInputValue(
        premiumFinanceAnalysis,
        "willingToProvidePremiumFinancingInfo_A_loanAmount",
      ),
    ),
    loanInterestRate: formatSimpleValue(
      getInputValue(
        premiumFinanceAnalysis,
        "willingToProvidePremiumFinancingInfo_A_loanInterestRate",
      ),
    ),
    loanTenor: formatSimpleValue(
      getInputValue(
        premiumFinanceAnalysis,
        "willingToProvidePremiumFinancingInfo_A_loanTenor",
      ),
    ),
    eachInstallment: formatSimpleValue(
      getInputValue(
        premiumFinanceAnalysis,
        "willingToProvidePremiumFinancingInfo_A_eachInstallment",
      ),
    ),
    confirmedImportantNotes: formatYesNoByKey(
      getSelectedKey(premiumFinanceAnalysis, "confirmedImportantNotes"),
      "A",
    ),
    hasSufficientFundsToRepayLoan: formatYesNoByKey(
      getSelectedKey(premiumFinanceAnalysis, "hasSufficientFundsToRepayLoan"),
      "A",
    ),
  };
}

function buildRecommendationSummary(suggestionsAndReasons: FormData[] | undefined) {
  const recommendationKey = getSelectedKey(
    suggestionsAndReasons,
    "reasonForTheRecommendation",
  );
  const selectedReasons = getSelectedKeys(
    suggestionsAndReasons,
    "reasonForTheRecommendation_B",
  ).map((key) => {
    const label = RECOMMENDATION_REASON_LABELS[key] ?? key;
    if (key === "B1_G") {
      const remark = getInputValue(
        suggestionsAndReasons,
        "reasonForTheRecommendation_B_B1_G_remark",
      );
      return remark ? `${label}：${remark}` : label;
    }
    if (key === "B2_E") {
      const remark = getInputValue(
        suggestionsAndReasons,
        "reasonForTheRecommendation_B_B2_E_remark",
      );
      return remark ? `${label}：${remark}` : label;
    }
    return label;
  });

  return {
    recommendation:
      recommendationKey ? RECOMMENDATION_LABELS[recommendationKey] ?? recommendationKey : "--",
    mismatchReasons:
      recommendationKey === "B" && selectedReasons.length
        ? selectedReasons.join("；")
        : "--",
  };
}

export function ZuuFnaSection({ data }: { data: ZuuFnaDataSource | null }) {
  const { message } = App.useApp();
  const [previewing, setPreviewing] = useState(false);

  const view = useMemo(
    () =>
      buildZuuFnaView(
        data?.financial?.financialAnalysis,
        data?.financial?.chooseProducts,
      ),
    [data?.financial?.chooseProducts, data?.financial?.financialAnalysis],
  );
  const premiumFinanceSummary = useMemo(
    () => buildPremiumFinanceSummary(data?.financial?.premiumFinanceAnalysis),
    [data?.financial?.premiumFinanceAnalysis],
  );
  const recommendationSummary = useMemo(
    () => buildRecommendationSummary(data?.financial?.suggestionsAndReasons),
    [data?.financial?.suggestionsAndReasons],
  );

  const historyCount = data?.historyFnaList?.length ?? 0;
  const hasContent =
    view.objectives.length > 0 ||
    view.introducedProducts.length > 0 ||
    view.selectedProducts.length > 0 ||
    (data?.financial?.premiumFinanceAnalysis?.length ?? 0) > 0 ||
    (data?.financial?.suggestionsAndReasons?.length ?? 0) > 0 ||
    historyCount > 0 ||
    Boolean(data?.sourceOrderId);

  const handleViewOriginal = async () => {
    if (!data?.sourceOrderId) {
      message.warning("無法獲取訂單資料");
      return;
    }

    try {
      setPreviewing(true);
      const result = await downloadOrderFormPdf({
        formType: "FNA",
        orderId: data.sourceOrderId,
      });

      const blobUrl = URL.createObjectURL(result.blob);
      const previewWindow = window.open(blobUrl, "_blank");

      if (!previewWindow) {
        message.warning("請允許彈出窗口以預覽檔案");
        URL.revokeObjectURL(blobUrl);
        return;
      }

      previewWindow.addEventListener("beforeunload", () => {
        URL.revokeObjectURL(blobUrl);
      });

      message.success("正在打開預覽窗口");
    } catch (error) {
      console.error("預覽FNA文件失敗:", error);
      message.error("檔案預覽失敗，請稍後重試");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <section className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <div>
          <h3 className={sectionStyles.title}>FNA</h3>
          <div className={sectionStyles.subtitle}>
            保留既有 FNA 問卷與產品資料，只將畫面改成 ZUU 專屬的分組查看頁。
          </div>
        </div>
        <div className={formStyles.headerActions}>
          <Button
            icon={<FileTextOutlined />}
            onClick={handleViewOriginal}
            loading={previewing}
            disabled={!data?.sourceOrderId}
            className={formStyles.previewButton}
          >
            查看原始檔案
          </Button>
          <div className={sectionStyles.badge}>{historyCount} 份歷史文件</div>
        </div>
      </div>

      {hasContent ? (
        <div className={formStyles.groups}>
          <div className={formStyles.group}>
            <div className={formStyles.groupHeader}>
              <div>
                <h4 className={formStyles.groupTitle}>問卷摘要</h4>
                <div className={formStyles.groupSubtitle}>
                  用只讀欄位方式回看保障期、繳費意願與來源訂單資訊。
                </div>
              </div>
              <div className={formStyles.groupBadge}>Summary</div>
            </div>
            <div className={formStyles.rows}>
              <QuestionRow label="目標保障 / 實現年期" value={view.coveragePeriod} />
              <QuestionRow label="保費支付意願" value={view.paymentPeriod} />
              <QuestionRow
                label="來源訂單"
                value={data?.sourceOrderId ? `#${data.sourceOrderId}` : "--"}
              />
              <QuestionRow label="歷史 FNA 文件" value={`${historyCount} 份`} />
            </div>
          </div>

          <div className={formStyles.group}>
            <div className={formStyles.groupHeader}>
              <div>
                <h4 className={formStyles.groupTitle}>購買目標</h4>
                <div className={formStyles.groupSubtitle}>
                  沿用原本 financialAnalysis 的問卷答案與金額邏輯，不重做字段語義。
                </div>
              </div>
              <div className={formStyles.groupBadge}>Objectives</div>
            </div>
            {view.objectives.length ? (
              <div className={formStyles.optionList}>
                {view.objectives.map((objective) => (
                  <div key={objective.key} className={formStyles.optionCard}>
                    <p className={formStyles.optionTitle}>{objective.title}</p>
                    <ul className={formStyles.optionDetails}>
                      {objective.details.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <div className={formStyles.rows}>
                <QuestionRow label="已填寫目標" value="--" />
              </div>
            )}
          </div>

          <ProductGroup
            title="介紹的產品"
            subtitle="保留既有 chooseProducts 順序，以只讀欄位顯示保司與建議期程。"
            badge="Products"
            products={view.introducedProducts}
          />

          <ProductGroup
            title="已選產品"
            subtitle="僅顯示 choose === true 的產品，閱讀節奏更接近表單查看頁。"
            badge="Selected"
            products={view.selectedProducts}
          />

          <div className={formStyles.group}>
            <div className={formStyles.groupHeader}>
              <div>
                <h4 className={formStyles.groupTitle}>保費融資</h4>
              </div>
              <div className={formStyles.groupBadge}>Premium Financing</div>
            </div>
            <div className={formStyles.rows}>
              <QuestionRow label="6.1 還款來源" value={premiumFinanceSummary.repaymentSource} />
              <QuestionRow
                label="6.2 a. 閣下未償還按揭貸款 不超過"
                value={premiumFinanceSummary.outstandingMortgageAmount}
              />
              <QuestionRow
                label="6.2 b. 閣下其他未償還貸款（不包括 Q 6.3 和 按揭貸款）不超過"
                value={premiumFinanceSummary.outstandingOtherLoansAmount}
              />
              <QuestionRow
                label="6.3 閣下願意盡閣下所知而提供的保費融資貸款預估資料如下"
                value={premiumFinanceSummary.willingToProvidePremiumFinancingInfo}
              />
              <QuestionRow label="貸方名稱" value={premiumFinanceSummary.lenderName} />
              <QuestionRow label="貸款金額" value={premiumFinanceSummary.loanAmount} />
              <QuestionRow label="貸款利率" value={premiumFinanceSummary.loanInterestRate} />
              <QuestionRow label="貸款期" value={premiumFinanceSummary.loanTenor} />
              <QuestionRow label="每期還款金額" value={premiumFinanceSummary.eachInstallment} />
              <QuestionRow
                label="6.4 已閱讀並理解“重要說明及影響”"
                value={premiumFinanceSummary.confirmedImportantNotes}
              />
              <QuestionRow
                label="6.5 有足夠資金全額償還貸款"
                value={premiumFinanceSummary.hasSufficientFundsToRepayLoan}
              />
            </div>
          </div>

          <div className={formStyles.group}>
            <div className={formStyles.groupHeader}>
              <div>
                <h4 className={formStyles.groupTitle}>建議及原因</h4>
              </div>
              <div className={formStyles.groupBadge}>Reasons</div>
            </div>
            <div className={formStyles.rows}>
              <QuestionRow
                label="中介人建議原因（可選多於一項）"
                value={recommendationSummary.recommendation}
              />
              <QuestionRow
                label="產品推介與申請人財務需要分析選項不相符之原因"
                value={recommendationSummary.mismatchReasons}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className={sectionStyles.empty}>
          <Empty description="暫無 FNA 數據" />
        </div>
      )}
    </section>
  );
}

function QuestionRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={formStyles.row}>
      <div className={formStyles.rowLabel}>{label}</div>
      <div className={value === "--" ? formStyles.emptyText : formStyles.rowValue}>
        {value}
      </div>
    </div>
  );
}

function ProductGroup({
  title,
  subtitle,
  badge,
  products,
}: {
  title: string;
  subtitle: string;
  badge: string;
  products: Array<{
    id: string;
    name: string;
    company: string;
    paymentPeriod: string;
    coveragePeriod: string;
  }>;
}) {
  return (
    <div className={formStyles.group}>
      <div className={formStyles.groupHeader}>
        <div>
          <h4 className={formStyles.groupTitle}>{title}</h4>
          <div className={formStyles.groupSubtitle}>{subtitle}</div>
        </div>
        <div className={formStyles.groupBadge}>{badge}</div>
      </div>
      {products.length ? (
        <div className={formStyles.productList}>
          {products.map((product) => (
            <div key={product.id} className={formStyles.productCard}>
              <p className={formStyles.productTitle}>{product.name}</p>
              <div className={formStyles.productMeta}>
                <div className={formStyles.productMetaItem}>
                  <span className={formStyles.productMetaLabel}>保險公司</span>
                  <span className={formStyles.productMetaValue}>{product.company}</span>
                </div>
                <div className={formStyles.productMetaItem}>
                  <span className={formStyles.productMetaLabel}>繳費期</span>
                  <span className={formStyles.productMetaValue}>{product.paymentPeriod}</span>
                </div>
                <div className={formStyles.productMetaItem}>
                  <span className={formStyles.productMetaLabel}>保障期</span>
                  <span className={formStyles.productMetaValue}>{product.coveragePeriod}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={formStyles.rows}>
          <QuestionRow label="產品資料" value="--" />
        </div>
      )}
    </div>
  );
}
